import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import ParticlesAuth from "./ParticlesAuth";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useAuthStore } from "../../stores/useAuthStore";
import { getRememberedUsername, clearRememberedUsername } from "../../helpers/rememberedIdentity";
import logoAgerouteLight from "../../assets/images/logo-ageroute-light.png";
import logoAgerouteDark from "../../assets/images/logo-ageroute-dark.png";

const WelcomeBackAlerte = () => {
  const navigate = useNavigate();
  const { error, errorMsg, loginUser, resetLoginFlag, loading } = useAuthStore();
  const [passwordShow, setPasswordShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const rememberedUsername = getRememberedUsername();

  useEffect(() => {
    if (!rememberedUsername) {
      navigate("/login", { replace: true });
    }
  }, [rememberedUsername, navigate]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { password: "" },
    validationSchema: Yup.object({
      password: Yup.string().required("Veuillez entrer votre mot de passe"),
    }),
    onSubmit: (values) => {
      if (!rememberedUsername) return;
      loginUser(
        { username: rememberedUsername, password: values.password },
        (path) => navigate(path),
        {}
      );
      setLoader(true);
    },
  });

  useEffect(() => {
    if (error && errorMsg) {
      toast.error(error);
      const t = setTimeout(() => {
        resetLoginFlag();
        setLoader(false);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [error, errorMsg, resetLoginFlag]);

  useEffect(() => {
    if (loading === false && loader) {
      setLoader(false);
    }
  }, [loading, loader]);

  const handleUseAnotherAccount = () => {
    clearRememberedUsername();
    navigate("/login", { replace: true });
  };

  document.title = "Bienvenue de retour | Road Alert AGEROUTE";

  if (!rememberedUsername) {
    return null;
  }

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div className="d-inline-block position-relative">
                  <Link to="/" className="auth-logo">
                    <img
                      src={logoAgerouteLight}
                      alt="AGEROUTE"
                      height="50"
                      className="logo-img-light"
                    />
                    <img
                      src={logoAgerouteDark}
                      alt="AGEROUTE"
                      height="50"
                      className="logo-img-dark position-absolute top-0 start-0"
                    />
                  </Link>
                </div>
                <p className="mt-3 fs-15 fw-medium text-white">
                  Plateforme d'Alerte Routière - AGEROUTE
                </p>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4 rounded-3 shadow-sm border-0">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <div className="avatar-lg mx-auto mb-3">
                      <div
                        className="avatar-title rounded-circle bg-primary bg-soft text-primary fs-1"
                        style={{ width: "80px", height: "80px", lineHeight: "80px" }}
                      >
                        <i className="ri-user-3-fill text-white" style={{ fontSize: "2.5rem" }}></i>
                      </div>
                    </div>
                    <h5 className="text-primary">Bienvenue {rememberedUsername.charAt(0).toUpperCase() + rememberedUsername.slice(1)} !</h5>
                    <p className="text-muted mb-1">
                      Reconnectez-vous avec votre mot de passe pour accéder à la plateforme d'alerte routière.
                    </p>
                  </div>
                  <div className="p-2 mt-4">
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                      action="#"
                    >
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="password-input">
                          Mot de passe
                        </Label>
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <Input
                            name="password"
                            id="password-input"
                            value={validation.values.password}
                            type={passwordShow ? "text" : "password"}
                            className="form-control pe-5"
                            placeholder="Entrez votre mot de passe"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.password && !!validation.errors.password
                            }
                          />
                          {validation.touched.password && validation.errors.password ? (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          ) : null}
                          <button
                            className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                            onClick={(e) => {
                              e.preventDefault();
                              setPasswordShow(!passwordShow);
                            }}
                            type="button"
                            id="password-addon"
                          >
                            <i className="ri-eye-fill align-middle"></i>
                          </button>
                        </div>
                      </div>

                      <div className="mt-4">
                        <Button
                          color="primary"
                          disabled={loader}
                          className="btn btn-primary w-100"
                          type="submit"
                          style={{ backgroundColor: "#311B91", borderColor: "#311B91" }}
                        >
                          {loader && (
                            <Spinner size="sm" className="me-2">
                              {" "}
                              Loading...{" "}
                            </Spinner>
                          )}
                          Se reconnecter
                        </Button>
                      </div>

                      <div className="mt-3 text-center">
                        <button
                          type="button"
                          className="btn btn-link text-muted p-0"
                          onClick={handleUseAnotherAccount}
                        >
                          Ce n'est pas vous ? Se connecter avec un autre compte
                        </button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default WelcomeBackAlerte;
